import type { SupportedLocale } from '@erp/shared';

const de = {
  roleTitle: 'Ordnerfunktion',
  roleEdit: 'Ordnerfunktion bearbeiten',
  roleAutomatic: 'Automatisch vom Server erkennen',
  roleSave: 'Ordnerfunktion speichern',
  roleSaved: 'Ordnerfunktion gespeichert',
  roleSaveFailed:
    'Die Ordnerfunktion konnte nicht gespeichert werden. Deine Angaben bleiben erhalten.',
  roleReasonRequired: 'Bitte gib eine Begründung mit mindestens drei Zeichen ein.',
  roleHint:
    'Lege die Funktion nur nach Prüfung mit dem Postfachinhaber fest. Die bewusste Zuordnung bleibt bei späteren Abrufen erhalten. Automatische Erkennung wird beim nächsten Abruf erneut geprüft.',

  title: 'Postfach synchronisieren',
  description: 'Verbindung, Abrufaufträge und Ordnerabdeckung prüfen.',
  scope:
    'Alle zugänglichen Ordner ab {{date}}, einschließlich Unterordnern, Archiv, Spam und Papierkorb. Ältere Nachrichten bleiben außerhalb dieses Importumfangs.',
  check: 'Verbindung prüfen',
  start: 'Jetzt synchronisieren',
  requesting: 'Wird angefordert …',
  requested: 'Abrufauftrag gespeichert. Der Fortschritt wird automatisch aktualisiert.',
  queueWaiting:
    'Abrufauftrag ist gespeichert. Die Hintergrundverarbeitung wird fortgesetzt, sobald die Warteschlange verfügbar ist.',
  requestFailed:
    'Abruf konnte nicht angefordert werden. Bitte erneut versuchen; der Auftrag wird nicht doppelt angelegt.',
  checkFailed:
    'Die Verbindung konnte nicht geprüft werden. Bitte Kontoeinstellungen und Berechtigungen prüfen.',
  connected:
    'Verbindung hergestellt: {{count}} zugängliche Nachrichtenordner gefunden. Es wurde keine E-Mail versendet.',
  loadFailed: 'Abrufprotokolle sind nicht verfügbar oder nicht mehr freigegeben.',
  empty:
    'Noch kein Abrufauftrag vorhanden. Prüfe die Verbindung und starte anschließend den ersten Import.',
  run: 'Abrufauftrag',
  progress: 'Abruffortschritt',
  counts:
    '{{completed}} von {{total}} Ordnern geprüft · {{observed}} Fundorte erfasst · {{imported}} Quellkopien importiert · {{skipped}} ältere Nachrichten ausgelassen',
  retryAt: 'Nächster Wiederholungsversuch frühestens {{date}}.',
  messageErrors: '{{count}} Nachrichten konnten nicht vollständig übernommen werden.',
  uid: 'Ordnerkennung {{uid}}',
  issuesLimited:
    'Die ersten Hinweise werden angezeigt. Weitere betroffene Nachrichten sind in der Fehleranzahl enthalten; ein erneuter Abruf prüft sie erneut.',
  folderStatuses: {
    PENDING: 'Ausstehend',
    RUNNING: 'In Prüfung',
    SUCCEEDED: 'Geprüft',
    PARTIAL: 'Teilweise geprüft',
    FAILED: 'Nicht geprüft',
    NOT_SELECTABLE: 'Übergeordneter Ordner ohne Nachrichten',
  },
  roleEvidence: {
    SPECIAL_USE: 'Serverkennzeichnung',
    PROVIDER: 'Protokollangabe',
    CONFIGURED: 'Bewusst zugeordnet',
    UNKNOWN: 'Funktion nicht bestätigt',
  },
  errors: {
    alreadyRunning: 'Für dieses Postfach läuft bereits ein Abrufauftrag.',
    runtimeDisabled: 'Der E-Mail-Abruf ist auf diesem Server ausgeschaltet.',
  },
  codes: {
    AUTHENTICATION_FAILED:
      'Anmeldung fehlgeschlagen. Zugangsdaten, App-Passwort oder Gültigkeit des Zugriffstokens prüfen.',
    TLS_FAILED:
      'Die verschlüsselte Verbindung konnte nicht sicher bestätigt werden. Serveradresse und Zertifikat prüfen.',
    CONNECTION_FAILED: 'Der Mailserver ist vorübergehend nicht erreichbar.',
    CONNECTION_TIMEOUT: 'Der Mailserver antwortet nicht rechtzeitig.',
    FOLDER_NOT_LISTED:
      'Ein zuvor sichtbarer Ordner wird nicht mehr aufgelistet. Löschung oder geänderte Providerrechte prüfen; die gespeicherte Historie bleibt erhalten.',
    WORKER_INTERRUPTED:
      'Die Hintergrundverarbeitung wurde wiederholt unterbrochen. Hinweise prüfen und bei Bedarf einen neuen Abruf starten.',
    NO_MESSAGE_FOLDERS:
      'Der Server listet keine auswählbaren Nachrichtenordner auf. Postfachumfang und Providerrechte prüfen.',
    FOLDER_ACCESS_DENIED:
      'Zugriff auf diesen Ordner verweigert. Ordnerrechte beim Mailanbieter prüfen.',
    INVALID_PROVIDER_DATA:
      'Der Server liefert unvollständige oder widersprüchliche Daten. Der Abruf wurde begrenzt.',
    MESSAGE_NOT_FOUND:
      'Die Nachricht wurde während des Abrufs verschoben oder entfernt. Ein erneuter Abruf prüft ihre Fundorte.',
    MESSAGE_TOO_LARGE:
      'Das Original überschreitet die zulässige Dateigröße und wurde nicht unvollständig gespeichert.',
    FLAGS_UNAVAILABLE:
      'Der Nachrichtenstatus fehlt. Entwürfe und gesendete Nachrichten können nicht sicher unterschieden werden.',
    INTERNAL_DATE_UNAVAILABLE:
      'Das Serverdatum fehlt. Die Nachricht kann dem gewählten Importzeitraum nicht sicher zugeordnet werden.',
    SOURCE_PROCESSING_FAILED: 'Die Originalnachricht konnte nicht sicher verarbeitet werden.',
    SOURCE_CONTENT_LIMITED:
      'Mindestens ein Text oder Anhang ist nur eingeschränkt verfügbar. Hinweise an der Nachricht prüfen.',
    FOLDER_ROLE_AMBIGUOUS: 'Der Server nennt widersprüchliche Ordnerfunktionen.',
    FOLDER_ROLE_UNCONFIRMED:
      'Die Ordnerfunktion ist nicht vom Server bestätigt. Der Name allein bestimmt ihre Funktion nicht.',
    FOLDER_LIMIT_EXCEEDED:
      'Die Zahl der Ordner überschreitet den kontrollierten Importumfang. Der Abruf ist unvollständig.',
    POLICY_DISABLED: 'Der Abruf ist wegen einer geänderten Freigabe oder Wartung pausiert.',
    ACCOUNT_CHANGED:
      'Das Postfach wurde geändert oder deaktiviert. Einstellungen prüfen und einen neuen Abruf starten.',
    MAIL_RUNTIME_DISABLED: 'Der E-Mail-Abruf ist auf diesem Server ausgeschaltet.',
    UNKNOWN: 'Der Abruf benötigt eine Prüfung. Kontoeinstellungen und Ordnerrechte kontrollieren.',
  },
};
type Texts = {
  [K in keyof typeof de]: (typeof de)[K] extends string
    ? string
    : { [P in keyof (typeof de)[K]]: string };
};
export const centralMailSyncTranslations = {
  de,
  en: {
    roleTitle: 'Folder role',
    roleEdit: 'Edit folder role',
    roleAutomatic: 'Detect automatically from server',
    roleSave: 'Save folder role',
    roleSaved: 'Folder role saved',
    roleSaveFailed: 'The folder role could not be saved. Your input is retained.',
    roleReasonRequired: 'Enter a reason with at least three characters.',
    roleHint:
      'Set a role only after confirming it with the mailbox owner. Explicit assignments survive later synchronization. Automatic detection is checked again on the next run.',

    title: 'Synchronize mailbox',
    description: 'Check the connection, synchronization requests and folder coverage.',
    scope:
      'Every accessible folder from {{date}}, including subfolders, archive, junk and trash. Older messages remain outside this import scope.',
    check: 'Check connection',
    start: 'Synchronize now',
    requesting: 'Requesting …',
    requested: 'Synchronization request saved. Progress updates automatically.',
    queueWaiting:
      'The request is saved. Background processing resumes when the queue becomes available.',
    requestFailed:
      'Synchronization could not be requested. Try again; the request will not be duplicated.',
    checkFailed: 'The connection could not be checked. Review account settings and permissions.',
    connected: 'Connected: {{count}} accessible message folders found. No email was sent.',
    loadFailed: 'Synchronization records are unavailable or no longer permitted.',
    empty: 'No synchronization request yet. Check the connection, then start the initial import.',
    run: 'Synchronization request',
    progress: 'Synchronization progress',
    counts:
      '{{completed}} of {{total}} folders checked · {{observed}} locations observed · {{imported}} source copies imported · {{skipped}} older messages excluded',
    retryAt: 'The next retry is due no earlier than {{date}}.',
    messageErrors: '{{count}} messages could not be imported completely.',
    uid: 'Folder identifier {{uid}}',
    issuesLimited:
      'The first findings are shown. Additional affected messages are included in the error count; another synchronization checks them again.',
    folderStatuses: {
      PENDING: 'Pending',
      RUNNING: 'Checking',
      SUCCEEDED: 'Checked',
      PARTIAL: 'Partially checked',
      FAILED: 'Not checked',
      NOT_SELECTABLE: 'Parent folder without messages',
    },
    roleEvidence: {
      SPECIAL_USE: 'Server designation',
      PROVIDER: 'Protocol information',
      CONFIGURED: 'Explicitly assigned',
      UNKNOWN: 'Role unconfirmed',
    },
    errors: {
      alreadyRunning: 'A synchronization request already exists for this mailbox.',
      runtimeDisabled: 'Mail synchronization is disabled on this server.',
    },
    codes: {
      AUTHENTICATION_FAILED:
        'Sign-in failed. Check credentials, app password or access-token expiry.',
      TLS_FAILED:
        'The encrypted connection could not be verified. Check the server address and certificate.',
      CONNECTION_FAILED: 'The mail server is temporarily unavailable.',
      CONNECTION_TIMEOUT: 'The mail server did not respond in time.',
      FOLDER_NOT_LISTED:
        'A previously visible folder is no longer listed. Check deletion or changed provider permissions; archived history is retained.',
      WORKER_INTERRUPTED:
        'Background processing was repeatedly interrupted. Review findings and deliberately start a new run if needed.',
      NO_MESSAGE_FOLDERS:
        'The server lists no selectable message folders. Check mailbox scope and provider permissions.',
      FOLDER_ACCESS_DENIED:
        'Access to this folder was denied. Check folder permissions with the mail provider.',
      INVALID_PROVIDER_DATA:
        'The server returned incomplete or conflicting data. Synchronization was limited.',
      MESSAGE_NOT_FOUND:
        'The message was moved or removed during synchronization. Another run checks its locations.',
      MESSAGE_TOO_LARGE:
        'The original exceeds the file-size limit and was not stored as incomplete evidence.',
      FLAGS_UNAVAILABLE:
        'Message status is missing. Drafts and sent messages cannot be distinguished reliably.',
      INTERNAL_DATE_UNAVAILABLE:
        'The server date is missing. The message cannot be assigned reliably to the import period.',
      SOURCE_PROCESSING_FAILED: 'The original message could not be processed safely.',
      SOURCE_CONTENT_LIMITED:
        'At least one body or attachment has limited availability. Check the message findings.',
      FOLDER_ROLE_AMBIGUOUS: 'The server reports conflicting folder roles.',
      FOLDER_ROLE_UNCONFIRMED:
        'The folder role is not confirmed by the server. Its name alone does not determine its role.',
      FOLDER_LIMIT_EXCEEDED:
        'The number of folders exceeds the controlled import scope. Synchronization is incomplete.',
      POLICY_DISABLED:
        'Synchronization is paused because permissions or maintenance policy changed.',
      ACCOUNT_CHANGED:
        'The mailbox was changed or disabled. Review settings and start a new synchronization.',
      MAIL_RUNTIME_DISABLED: 'Mail synchronization is disabled on this server.',
      UNKNOWN: 'Synchronization needs review. Check account settings and folder permissions.',
    },
  },
  fr: {
    roleTitle: 'Fonction du dossier',
    roleEdit: 'Modifier la fonction',
    roleAutomatic: 'Détecter automatiquement sur le serveur',
    roleSave: 'Enregistrer la fonction',
    roleSaved: 'Fonction enregistrée',
    roleSaveFailed: 'Impossible d’enregistrer la fonction. Vos saisies sont conservées.',
    roleReasonRequired: 'Saisissez une justification d’au moins trois caractères.',
    roleHint:
      'Définissez la fonction après confirmation du propriétaire. Les attributions explicites sont conservées lors des synchronisations suivantes. La détection automatique reprend au prochain passage.',

    title: 'Synchroniser la boîte',
    description: 'Vérifier la connexion, les demandes et la couverture des dossiers.',
    scope:
      'Tous les dossiers accessibles depuis le {{date}}, y compris sous-dossiers, archives, indésirables et corbeille. Les messages plus anciens restent hors du périmètre.',
    check: 'Vérifier la connexion',
    start: 'Synchroniser maintenant',
    requesting: 'Demande en cours …',
    requested: 'Demande enregistrée. La progression est actualisée automatiquement.',
    queueWaiting:
      'La demande est enregistrée. Le traitement reprend dès que la file est disponible.',
    requestFailed:
      'Impossible de demander la synchronisation. Réessayez ; la demande ne sera pas dupliquée.',
    checkFailed:
      'Impossible de vérifier la connexion. Contrôlez les paramètres et les autorisations.',
    connected:
      'Connexion établie : {{count}} dossiers de messages accessibles. Aucun e-mail envoyé.',
    loadFailed: 'Les journaux sont indisponibles ou leur accès a été retiré.',
    empty: 'Aucune demande. Vérifiez la connexion puis lancez le premier import.',
    run: 'Demande de synchronisation',
    progress: 'Progression',
    counts:
      '{{completed}} dossiers sur {{total}} vérifiés · {{observed}} emplacements observés · {{imported}} copies sources importées · {{skipped}} messages anciens exclus',
    retryAt: 'Nouvelle tentative au plus tôt le {{date}}.',
    messageErrors: '{{count}} messages n’ont pas pu être importés entièrement.',
    uid: 'Identifiant du dossier {{uid}}',
    issuesLimited:
      'Les premiers constats sont affichés. Le total inclut les autres messages concernés ; une nouvelle synchronisation les vérifie à nouveau.',
    folderStatuses: {
      PENDING: 'En attente',
      RUNNING: 'Vérification',
      SUCCEEDED: 'Vérifié',
      PARTIAL: 'Vérifié partiellement',
      FAILED: 'Non vérifié',
      NOT_SELECTABLE: 'Dossier parent sans messages',
    },
    roleEvidence: {
      SPECIAL_USE: 'Indication du serveur',
      PROVIDER: 'Donnée du protocole',
      CONFIGURED: 'Attribution explicite',
      UNKNOWN: 'Fonction non confirmée',
    },
    errors: {
      alreadyRunning: 'Une demande est déjà active pour cette boîte.',
      runtimeDisabled: 'La synchronisation des e-mails est désactivée sur ce serveur.',
    },
    codes: {
      AUTHENTICATION_FAILED:
        'Échec de connexion. Vérifiez les identifiants, le mot de passe d’application ou l’expiration du jeton.',
      TLS_FAILED:
        'La connexion chiffrée n’a pas pu être vérifiée. Contrôlez l’adresse du serveur et son certificat.',
      CONNECTION_FAILED: 'Le serveur de messagerie est temporairement indisponible.',
      CONNECTION_TIMEOUT: 'Le serveur n’a pas répondu à temps.',
      FOLDER_NOT_LISTED:
        'Un dossier précédemment visible n’est plus listé. Vérifiez sa suppression ou ses droits ; l’historique archivé est conservé.',
      WORKER_INTERRUPTED:
        'Le traitement a été interrompu plusieurs fois. Vérifiez les constats puis lancez explicitement une nouvelle demande si nécessaire.',
      NO_MESSAGE_FOLDERS:
        'Le serveur ne liste aucun dossier de messages sélectionnable. Vérifiez le périmètre et les droits du fournisseur.',
      FOLDER_ACCESS_DENIED:
        'Accès à ce dossier refusé. Vérifiez ses autorisations auprès du fournisseur.',
      INVALID_PROVIDER_DATA:
        'Le serveur fournit des données incomplètes ou contradictoires. La synchronisation a été limitée.',
      MESSAGE_NOT_FOUND:
        'Le message a été déplacé ou supprimé pendant le traitement. Une nouvelle synchronisation vérifie ses emplacements.',
      MESSAGE_TOO_LARGE:
        'L’original dépasse la taille autorisée ; aucune preuve tronquée n’a été conservée.',
      FLAGS_UNAVAILABLE:
        'Le statut du message manque. Impossible de distinguer sûrement brouillons et messages envoyés.',
      INTERNAL_DATE_UNAVAILABLE:
        'La date du serveur manque. Impossible de rattacher sûrement le message à la période d’import.',
      SOURCE_PROCESSING_FAILED: 'Le message original n’a pas pu être traité en sécurité.',
      SOURCE_CONTENT_LIMITED:
        'Un texte ou une pièce jointe est partiellement disponible. Consultez les constats du message.',
      FOLDER_ROLE_AMBIGUOUS: 'Le serveur indique des fonctions de dossier contradictoires.',
      FOLDER_ROLE_UNCONFIRMED:
        'La fonction du dossier n’est pas confirmée par le serveur. Son nom seul ne suffit pas.',
      FOLDER_LIMIT_EXCEEDED:
        'Le nombre de dossiers dépasse le périmètre contrôlé. La synchronisation est incomplète.',
      POLICY_DISABLED:
        'Synchronisation suspendue après modification des droits ou activation de la maintenance.',
      ACCOUNT_CHANGED:
        'La boîte a été modifiée ou désactivée. Vérifiez ses paramètres et lancez une nouvelle synchronisation.',
      MAIL_RUNTIME_DISABLED: 'La synchronisation des e-mails est désactivée sur ce serveur.',
      UNKNOWN:
        'Une vérification est nécessaire. Contrôlez les paramètres et les droits des dossiers.',
    },
  },
  es: {
    roleTitle: 'Función de carpeta',
    roleEdit: 'Editar función de carpeta',
    roleAutomatic: 'Detectar automáticamente del servidor',
    roleSave: 'Guardar función',
    roleSaved: 'Función guardada',
    roleSaveFailed: 'No se pudo guardar la función. Se conservan tus datos.',
    roleReasonRequired: 'Introduce un motivo de al menos tres caracteres.',
    roleHint:
      'Define la función tras confirmarla con el propietario. Las asignaciones explícitas se conservan en futuras sincronizaciones. La detección automática se revisa en la siguiente ejecución.',

    title: 'Sincronizar buzón',
    description: 'Comprueba la conexión, las solicitudes y la cobertura de carpetas.',
    scope:
      'Todas las carpetas accesibles desde {{date}}, incluidas subcarpetas, archivo, correo no deseado y papelera. Los mensajes anteriores quedan fuera del alcance.',
    check: 'Comprobar conexión',
    start: 'Sincronizar ahora',
    requesting: 'Solicitando …',
    requested: 'Solicitud guardada. El progreso se actualiza automáticamente.',
    queueWaiting:
      'La solicitud está guardada. El procesamiento continuará cuando la cola esté disponible.',
    requestFailed:
      'No se pudo solicitar la sincronización. Reinténtalo; no se duplicará la solicitud.',
    checkFailed: 'No se pudo comprobar la conexión. Revisa la configuración y los permisos.',
    connected:
      'Conexión establecida: {{count}} carpetas de mensajes accesibles. No se ha enviado ningún correo.',
    loadFailed: 'Los registros no están disponibles o ya no tienes acceso.',
    empty: 'Todavía no hay solicitudes. Comprueba la conexión e inicia la primera importación.',
    run: 'Solicitud de sincronización',
    progress: 'Progreso',
    counts:
      '{{completed}} de {{total}} carpetas comprobadas · {{observed}} ubicaciones observadas · {{imported}} copias de origen importadas · {{skipped}} mensajes anteriores excluidos',
    retryAt: 'El siguiente intento no se realizará antes de {{date}}.',
    messageErrors: 'No se han podido importar completamente {{count}} mensajes.',
    uid: 'Identificador de carpeta {{uid}}',
    issuesLimited:
      'Se muestran los primeros avisos. El total incluye los demás mensajes afectados; otra sincronización volverá a comprobarlos.',
    folderStatuses: {
      PENDING: 'Pendiente',
      RUNNING: 'Comprobando',
      SUCCEEDED: 'Comprobada',
      PARTIAL: 'Comprobada parcialmente',
      FAILED: 'Sin comprobar',
      NOT_SELECTABLE: 'Carpeta principal sin mensajes',
    },
    roleEvidence: {
      SPECIAL_USE: 'Indicación del servidor',
      PROVIDER: 'Información del protocolo',
      CONFIGURED: 'Asignación explícita',
      UNKNOWN: 'Función sin confirmar',
    },
    errors: {
      alreadyRunning: 'Ya existe una solicitud activa para este buzón.',
      runtimeDisabled: 'La sincronización de correo está desactivada en este servidor.',
    },
    codes: {
      AUTHENTICATION_FAILED:
        'Error de autenticación. Comprueba las credenciales, la contraseña de aplicación o la caducidad del token.',
      TLS_FAILED:
        'No se pudo verificar la conexión cifrada. Comprueba la dirección y el certificado del servidor.',
      CONNECTION_FAILED: 'El servidor de correo no está disponible temporalmente.',
      CONNECTION_TIMEOUT: 'El servidor no ha respondido a tiempo.',
      FOLDER_NOT_LISTED:
        'Una carpeta antes visible ya no aparece. Comprueba su eliminación o los cambios de permisos; el historial archivado se conserva.',
      WORKER_INTERRUPTED:
        'El procesamiento se interrumpió varias veces. Revisa los avisos e inicia expresamente otra ejecución si es necesario.',
      NO_MESSAGE_FOLDERS:
        'El servidor no muestra carpetas de mensajes seleccionables. Revisa el alcance del buzón y los permisos.',
      FOLDER_ACCESS_DENIED: 'Acceso denegado a esta carpeta. Revisa sus permisos con el proveedor.',
      INVALID_PROVIDER_DATA:
        'El servidor devuelve datos incompletos o contradictorios. Se ha limitado la sincronización.',
      MESSAGE_NOT_FOUND:
        'El mensaje se movió o eliminó durante la sincronización. Otra ejecución comprobará sus ubicaciones.',
      MESSAGE_TOO_LARGE: 'El original supera el tamaño permitido y no se ha guardado truncado.',
      FLAGS_UNAVAILABLE:
        'Falta el estado del mensaje. No se pueden distinguir con seguridad los borradores y los mensajes enviados.',
      INTERNAL_DATE_UNAVAILABLE:
        'Falta la fecha del servidor. No se puede asignar el mensaje con seguridad al período de importación.',
      SOURCE_PROCESSING_FAILED: 'No se pudo procesar el mensaje original de forma segura.',
      SOURCE_CONTENT_LIMITED:
        'Hay un texto o adjunto con disponibilidad limitada. Revisa los avisos del mensaje.',
      FOLDER_ROLE_AMBIGUOUS: 'El servidor indica funciones contradictorias para la carpeta.',
      FOLDER_ROLE_UNCONFIRMED:
        'El servidor no confirma la función de la carpeta. Su nombre por sí solo no determina su función.',
      FOLDER_LIMIT_EXCEEDED:
        'El número de carpetas supera el alcance controlado. La sincronización está incompleta.',
      POLICY_DISABLED: 'Sincronización pausada por cambios de permisos o mantenimiento.',
      ACCOUNT_CHANGED:
        'El buzón ha cambiado o se ha desactivado. Revisa los ajustes e inicia otra sincronización.',
      MAIL_RUNTIME_DISABLED: 'La sincronización de correo está desactivada en este servidor.',
      UNKNOWN:
        'Es necesario revisar la sincronización. Comprueba los ajustes y los permisos de las carpetas.',
    },
  },
  tr: {
    roleTitle: 'Klasör işlevi',
    roleEdit: 'Klasör işlevini düzenle',
    roleAutomatic: 'Sunucudan otomatik algıla',
    roleSave: 'Klasör işlevini kaydet',
    roleSaved: 'Klasör işlevi kaydedildi',
    roleSaveFailed: 'Klasör işlevi kaydedilemedi. Girdileriniz korundu.',
    roleReasonRequired: 'En az üç karakterli bir gerekçe girin.',
    roleHint:
      'İşlevi posta kutusu sahibiyle doğruladıktan sonra belirleyin. Açık atamalar sonraki eşitlemelerde korunur. Otomatik algılama bir sonraki çalıştırmada yeniden denetlenir.',

    title: 'Posta kutusunu eşitle',
    description: 'Bağlantıyı, eşitleme isteklerini ve klasör kapsamını denetleyin.',
    scope:
      '{{date}} tarihinden itibaren alt klasörler, arşiv, spam ve çöp kutusu dahil erişilebilir tüm klasörler. Daha eski iletiler bu kapsamın dışındadır.',
    check: 'Bağlantıyı denetle',
    start: 'Şimdi eşitle',
    requesting: 'İsteniyor …',
    requested: 'Eşitleme isteği kaydedildi. İlerleme otomatik güncellenir.',
    queueWaiting: 'İstek kaydedildi. Kuyruk kullanılabilir olduğunda arka plan işlemesi sürer.',
    requestFailed: 'Eşitleme istenemedi. Yeniden deneyin; yinelenen istek oluşturulmaz.',
    checkFailed: 'Bağlantı denetlenemedi. Hesap ayarlarını ve yetkileri gözden geçirin.',
    connected:
      'Bağlantı kuruldu: erişilebilir {{count}} ileti klasörü bulundu. E-posta gönderilmedi.',
    loadFailed: 'Eşitleme kayıtlarına erişilemiyor veya erişim izni kaldırıldı.',
    empty: 'Henüz eşitleme isteği yok. Bağlantıyı denetleyip ilk içe aktarımı başlatın.',
    run: 'Eşitleme isteği',
    progress: 'Eşitleme ilerlemesi',
    counts:
      '{{total}} klasörden {{completed}} tanesi denetlendi · {{observed}} konum gözlendi · {{imported}} kaynak kopyası alındı · {{skipped}} eski ileti kapsam dışında bırakıldı',
    retryAt: 'Sonraki deneme en erken {{date}} tarihinde.',
    messageErrors: '{{count}} ileti tamamen içe aktarılamadı.',
    uid: 'Klasör kimliği {{uid}}',
    issuesLimited:
      'İlk bulgular gösterilir. Diğer etkilenen iletiler hata sayısına dahildir; yeni bir eşitleme bunları yeniden denetler.',
    folderStatuses: {
      PENDING: 'Bekliyor',
      RUNNING: 'Denetleniyor',
      SUCCEEDED: 'Denetlendi',
      PARTIAL: 'Kısmen denetlendi',
      FAILED: 'Denetlenemedi',
      NOT_SELECTABLE: 'İleti içermeyen üst klasör',
    },
    roleEvidence: {
      SPECIAL_USE: 'Sunucu işareti',
      PROVIDER: 'Protokol bilgisi',
      CONFIGURED: 'Açık atama',
      UNKNOWN: 'İşlev doğrulanmadı',
    },
    errors: {
      alreadyRunning: 'Bu posta kutusu için etkin bir eşitleme isteği zaten var.',
      runtimeDisabled: 'Bu sunucuda e-posta eşitlemesi kapalı.',
    },
    codes: {
      AUTHENTICATION_FAILED:
        'Oturum açılamadı. Kimlik bilgilerini, uygulama parolasını veya erişim belirtecinin süresini denetleyin.',
      TLS_FAILED: 'Şifreli bağlantı doğrulanamadı. Sunucu adresini ve sertifikasını denetleyin.',
      CONNECTION_FAILED: 'Posta sunucusuna geçici olarak erişilemiyor.',
      CONNECTION_TIMEOUT: 'Posta sunucusu zamanında yanıt vermedi.',
      FOLDER_NOT_LISTED:
        'Daha önce görünen bir klasör artık listelenmiyor. Silinme veya sağlayıcı izinlerini denetleyin; arşiv geçmişi korunur.',
      WORKER_INTERRUPTED:
        'Arka plan işlemesi tekrar tekrar kesildi. Bulguları inceleyin ve gerekirse bilinçli olarak yeni bir eşitleme başlatın.',
      NO_MESSAGE_FOLDERS:
        'Sunucu seçilebilir ileti klasörü listelemiyor. Posta kutusu kapsamını ve sağlayıcı izinlerini denetleyin.',
      FOLDER_ACCESS_DENIED:
        'Bu klasöre erişim reddedildi. Posta sağlayıcısındaki klasör izinlerini denetleyin.',
      INVALID_PROVIDER_DATA: 'Sunucu eksik veya çelişkili veri sağladı. Eşitleme sınırlandırıldı.',
      MESSAGE_NOT_FOUND:
        'İleti eşitleme sırasında taşındı veya silindi. Sonraki eşitleme konumlarını denetler.',
      MESSAGE_TOO_LARGE:
        'Özgün dosya izin verilen boyutu aşıyor; kesilmiş kanıt olarak kaydedilmedi.',
      FLAGS_UNAVAILABLE:
        'İleti durumu eksik. Taslaklar ve gönderilmiş iletiler güvenle ayırt edilemiyor.',
      INTERNAL_DATE_UNAVAILABLE:
        'Sunucu tarihi eksik. İleti içe aktarım dönemine güvenle atanamıyor.',
      SOURCE_PROCESSING_FAILED: 'Özgün ileti güvenli biçimde işlenemedi.',
      SOURCE_CONTENT_LIMITED:
        'En az bir metin veya ek sınırlı olarak kullanılabilir. İletideki bulguları denetleyin.',
      FOLDER_ROLE_AMBIGUOUS: 'Sunucu çelişkili klasör işlevleri bildiriyor.',
      FOLDER_ROLE_UNCONFIRMED:
        'Klasörün işlevi sunucu tarafından doğrulanmadı. Yalnızca adı işlevini belirlemez.',
      FOLDER_LIMIT_EXCEEDED:
        'Klasör sayısı kontrollü içe aktarım kapsamını aşıyor. Eşitleme tamamlanmadı.',
      POLICY_DISABLED: 'Yetki değişikliği veya bakım nedeniyle eşitleme duraklatıldı.',
      ACCOUNT_CHANGED:
        'Posta kutusu değiştirildi veya kapatıldı. Ayarları denetleyip yeni eşitleme başlatın.',
      MAIL_RUNTIME_DISABLED: 'Bu sunucuda e-posta eşitlemesi kapalı.',
      UNKNOWN: 'Eşitleme gözden geçirilmeli. Hesap ayarlarını ve klasör izinlerini denetleyin.',
    },
  },
} satisfies Record<SupportedLocale, Texts>;
